import { createBrowserRouter, Navigate, Outlet, useParams, useSearchParams } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import routes from "./routes";
import ConditionalRoute from "@/components/ConditionalRoute/ConditionalRoute";
import PreLogin from "@/layouts/PreLogin/PreLogin";
import PostLogin from "@/layouts/PostLogin/PostLogin";
import PageNotFound from "@/pages/PageNotFound/PageNotFound";
import type { RouteConfig } from "@/types/routestypes";
import ProtectedRoute from "./protectedRoute";
import MobileViewGuard from "@/routes/MobileViewGuard";
import Unauthorized from "@/pages/Unauthorized/Unauthorized";
import { lazy } from "react";
import TimelineDrawer from "@/pages/Drawer/CustomerDrawer/TimelineDrawer";

const LeaveCalendar = lazy(() => import("@/pages/LeaveManagement/LeaveCalendar"));
const LeadFormRenderer = lazy(() => import("@/pages/LeadCapture/EmbedCode/LeadFormRenderer"));

const MobileTimelineDrawerView = () => {
  const { leadId } = useParams();
  const [searchParams] = useSearchParams();

  const parsedLeadId = Number(leadId);
  if (!parsedLeadId) {
    return <Navigate to="/dashboard" replace />;
  }

  const leadName = searchParams.get("leadName") || "";
  const type = searchParams.get("type") || "Customer";
  const oppNumber = Number(searchParams.get("oppNumber") || 0);
  const phoneNumber = searchParams.get("phoneNumber") || "";
  const isCustomer = type === "Customer";

  return (
    <TimelineDrawer
      statusList={[]}
      visitPurposeList={[]}
      leadId={parsedLeadId}
      leadName={leadName}
      oppNumber={oppNumber}
      type={type}
      phoneNumber={phoneNumber}
      isCustomer={isCustomer}
      insightsOnly
      hideHeader
    />
  );
};

const buildRoutes = (
  routesInput: Record<string, RouteConfig> | RouteConfig[],
  isPrivate: boolean = false
): RouteObject[] => {
  const routesArray: RouteConfig[] = Array.isArray(routesInput)
    ? routesInput
    : Object.values(routesInput);

  return routesArray
    .map(({
      path,
      element,
      children,
      permission,
    }) => {
      let wrappedElement = element ?? <Outlet />;

      if (isPrivate && permission) {
        wrappedElement = (
          <ProtectedRoute permission={permission}>
            {wrappedElement}
          </ProtectedRoute>
        );
      }

      return {
        path,
        element: wrappedElement,
        ...(children
          ? {
            children: buildRoutes(children, isPrivate)
          }
          : {}),
      };
    });
};

const router = createBrowserRouter([
  // Public routes with Prelogin layout
  {
    path: "/auth",
    element: <ConditionalRoute component={PreLogin} />,
    children: [
      ...buildRoutes(routes.publicRoutes, false),
      {
        index: true,
        element: <Navigate to="/auth/login" replace />
      },
    ],
  },

  // Public Embed Route
  {
    path: "embed/:formCode",
    element: <LeadFormRenderer />,
  },

  // Private routes with PostLogin layout
  {
    path: "/",
    element: <ConditionalRoute isPrivate component={PostLogin} />,
    children: [
      ...buildRoutes(routes.privateRoutes, true),
      {
        index: true,
        element: (
          <Navigate to="/dashboard" replace />
        ),
      },
    ],
  },

  {
    path: "leavecalendar-mobile",
    element: (
      <MobileViewGuard>
        <LeaveCalendar />
      </MobileViewGuard>
    ),
  },

  {
    path: "customerInsights/:leadId",
    element: (
      <MobileViewGuard>
        <MobileTimelineDrawerView />
      </MobileViewGuard>
    ),
  },

  {
    path: "/unauthorized",
    element: <Unauthorized />,
  },

  // Catch-all
  {
    path: "*",
    element: <PageNotFound />,
  },
]);

export default router;
